import React, { useState, useEffect } from 'react'
import { Button, Card, Input, Typography, Switch, Form, message } from 'antd'
import { portalAccentGhostButtonStyle, useTheme, useThemeColors } from '../../common/ThemeContext'
import OrgLogo from '../../common/components/Logo/OrganizationLogo'
import CheckinCheckoutHeader from '../../common/components/Header/CheckinCheckoutHeader'
import FooterAllRights from '../../common/components/Footer/FooterAllRights'
import axios from 'axios'
import { config } from '../../Configs/firebase.config'
import { httpGet } from '../../common/http.service'

const { Title } = Typography
const { TextArea } = Input

const apiEndpoint = `${config.baseUrl}/api/v1/client/checkin`

function CheckInForm() {
	const { theme } = useTheme()
	const themeColors = useThemeColors()
	const { primary } = themeColors
	const ghostBtn = portalAccentGhostButtonStyle(theme, primary)
	const [staffId, setStaffId] = useState<string>('')
	const [isNightShiftEmg, setIsNightShiftEmg] = useState<boolean>(false)
	const [checkinReason, setCheckinReason] = useState<string>('')
	const [loading, setLoading] = useState(false)
	const [checkInStatus, setCheckInStatus] = useState<'success' | 'warning'>('success')
	const [checkInMsg, setCheckInMsg] = useState({ title: '', description: '' })
	const [showCheckInMsg, setShowCheckInMsg] = useState(false)
	const [orgLocation, setOrgLocation] = useState<{ latitude: string; longitude: string } | null>(
		null,
	)

	useEffect(() => {
		fetchOrganizationLocation()
	}, [])

	const fetchOrganizationLocation = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/org')
			if (response?.data) {
				setOrgLocation({
					latitude: response.data.latitude?.toString() || '',
					longitude: response.data.longitude?.toString() || '',
				})
			}
		} catch (error: any) {
			console.error('Failed to fetch organization location:', error)
			message.error('Failed to load organization information')
		}
	}

	const nightShiftEmgChange = (checked: boolean) => {
		setIsNightShiftEmg(checked)
	}

	const checkIn = async () => {
		if (!staffId) return
		if (!orgLocation) {
			message.error('Organization location not available')
			return
		}
		setLoading(true)
		const body = {
			staffId,
			isExtraNightShift: isNightShiftEmg,
			checkinReason,
			checkoutReason: '',
			latitude: orgLocation.latitude,
			longitude: orgLocation.longitude,
		}

		try {
			const res = await axios.post(apiEndpoint, body)
			setCheckInMsg({
				title: 'Check-In Successful',
				description: res.data.data.message,
			})
			setCheckInStatus('success')
		} catch (error: any) {
			setCheckInMsg({
				title: 'Check-In Failed',
				description: `${
					error.response?.data?.message[0] || 'Unknown error'
				}. Staff ID: ${staffId}.`,
			})
			setCheckInStatus('warning')
		} finally {
			setShowCheckInMsg(true)
			setLoading(false)
		}
	}

	const isReasonRequired = checkInMsg.description.includes('You are late')

	return (
		<>
			<CheckinCheckoutHeader />

			{!showCheckInMsg ? (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: 'calc(100vh - 120px)',
					}}
				>
					<Card
						style={{
							width: '90%',
							maxWidth: '400px',
							textAlign: 'center',
							borderRadius: '12px',
							boxShadow: themeColors.cardShadow,
							background: themeColors.bg,
							borderColor: themeColors.border,
						}}
					>
						<OrgLogo />
						<Title
							level={2}
							style={{
								color: primary,
								marginTop: '20px',
								fontFamily: 'Times New Roman',
							}}
						>
							Check In
						</Title>
						<Form layout='vertical'>
							{/* Staff ID Input */}
							<Form.Item label='Staff ID'>
								<Input
									placeholder='Input staff ID here'
									value={staffId}
									onChange={(e) => setStaffId(e.target.value)}
								/>
							</Form.Item>

							{/* Night Shift Toggle */}
							<Form.Item label='Night Shift Emergency'>
								<Switch checked={isNightShiftEmg} onChange={nightShiftEmgChange} />
							</Form.Item>

							{/* Check In Button */}
							<Form.Item>
								<Button
									loading={loading}
									disabled={!staffId}
									onClick={checkIn}
									style={{ ...ghostBtn, width: '100%' }}
								>
									Check In
								</Button>
							</Form.Item>
						</Form>
						<FooterAllRights />
					</Card>
				</div>
			) : (
				<Card
					style={{
						margin: '20px',
						textAlign: 'center',
						background: themeColors.bg,
						borderColor: themeColors.border,
					}}
				>
					<Title
						level={4}
						style={{
							color: checkInStatus === 'success' ? 'green' : 'red',
							fontFamily: 'Times New Roman',
						}}
					>
						{checkInMsg.title}
					</Title>
					<Typography style={{ color: themeColors.text }}>{checkInMsg.description}</Typography>
					{isReasonRequired && (
						<Form layout='vertical' style={{ marginTop: '20px' }}>
							<Form.Item label='Reason for Late Check In'>
								<TextArea
									placeholder='Write your reason here'
									value={checkinReason}
									onChange={(e) => setCheckinReason(e.target.value)}
									rows={4}
								/>
							</Form.Item>
							<Button
								disabled={!checkinReason}
								onClick={checkIn}
								style={{ ...ghostBtn, width: '100%' }}
							>
								Submit
							</Button>
						</Form>
					)}
					<FooterAllRights />
				</Card>
			)}
		</>
	)
}

export default CheckInForm
